import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { AdminQueryError } from '../../components/admin/AdminQueryError';
import { api } from '../../lib/api';
import { ResponsiveTable, type ResponsiveColumn } from '../../components/common/ResponsiveTable';
import type { AdminBacklinkOrder, BacklinkGrade } from '../../types/marketing';

const money = (value: string | number) => `$${Number(value).toFixed(2)}`;

const STATUS_META: Record<string, { label: string; color: 'warning' | 'success' | 'neutral' }> = {
  in_process: { label: 'In Process', color: 'warning' },
  complete: { label: 'Complete', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'neutral' },
};

/** Grade pricing editor — what we charge per link, platform-wide. */
const GradePricingCard: React.FC = () => {
  const queryClient = useQueryClient();
  const [prices, setPrices] = useState<Record<number, string>>({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const { data: grades = [], isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['backlink-grades'],
    queryFn: async () => {
      const response = await api.get<any>('/api/marketing/backlink-grades/');
      const data = response.data;
      return (data.results || data) as BacklinkGrade[];
    },
  });

  const savePrices = useMutation({
    mutationFn: async () => {
      const changed = grades.filter(
        (g) => prices[g.id] !== undefined && Number(prices[g.id]) !== Number(g.price)
      );
      for (const grade of changed) {
        await api.patch(`/api/marketing/backlink-grades/${grade.id}/`, { price: prices[grade.id] });
      }
      return changed.length;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backlink-grades'] });
      setPrices({});
      setSaved(true);
      setError('');
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err: any) => {
      setError(err.response?.data?.price?.[0] || 'Failed to save prices.');
    },
  });

  const invalidPrices = Object.values(prices).some(price => !price.trim() || !Number.isFinite(Number(price)) || Number(price) < 1);
  const hasChanges = grades.some(
    (g) => prices[g.id] !== undefined && prices[g.id] !== '' && Number(prices[g.id]) !== Number(g.price)
  );

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography level="title-lg">Backlink Pricing</Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            What organizations pay per link at each grade. Defaults are vendor cost + 40%.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {saved && <Chip color="success" size="sm" variant="soft">Saved</Chip>}
          <Button
            size="sm"
            disabled={!hasChanges || invalidPrices || !!loadError}
            loading={savePrices.isPending}
            onClick={() => savePrices.mutate()}
          >
            Save Prices
          </Button>
        </Box>
      </Box>
      <AdminQueryError error={loadError} retry={refetch} message="Could not load backlink prices." />
      {invalidPrices && <Alert color="warning">Every price must be a number of at least $1.</Alert>}
      {error && <Alert color="danger" size="sm">{error}</Alert>}
      {isLoading ? (
        <CircularProgress size="sm" />
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
          {grades.map((grade) => (
            <FormControl key={grade.id} size="sm" sx={{ width: 150 }}>
              <FormLabel>
                {grade.name}
                {!grade.is_active && ' (inactive)'}
              </FormLabel>
              <Input
                size="sm"
                disabled={savePrices.isPending}
                type="number"
                slotProps={{ input: { min: 1, step: 0.5 } }}
                startDecorator="$"
                value={prices[grade.id] ?? grade.price}
                onChange={(e) => setPrices((prev) => ({ ...prev, [grade.id]: e.target.value }))}
              />
            </FormControl>
          ))}
        </Box>
      )}
    </Card>
  );
};

/** Fulfillment modal: record published target domains, notes, and status. */
const OrderFulfillmentModal: React.FC<{
  order: AdminBacklinkOrder;
  onClose: () => void;
}> = ({ order, onClose }) => {
  const queryClient = useQueryClient();
  const [targetDomains, setTargetDomains] = useState<Record<number, string>>(
    () => Object.fromEntries(order.items.map((item) => [item.id, item.target_domain]))
  );
  const [adminNotes, setAdminNotes] = useState(order.admin_notes);
  const [error, setError] = useState('');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-backlink-orders'] });
  };

  const saveDetails = useMutation({
    mutationFn: async () => {
      await api.patch(`/api/marketing/admin/backlink-orders/${order.id}/items/`, {
        items: order.items.map((item) => ({ id: item.id, target_domain: (targetDomains[item.id] || '').trim() })),
      });
      await api.patch(`/api/marketing/admin/backlink-orders/${order.id}/`, { admin_notes: adminNotes });
    },
    onSuccess: () => { invalidate(); setError(''); },
    onError: (err: any) => setError(err.response?.data?.error || 'Failed to save order details.'),
  });

  const setStatus = useMutation({
    mutationFn: async (status: string) => {
      // Persist any target-domain edits first so completion validation sees them
      await api.patch(`/api/marketing/admin/backlink-orders/${order.id}/items/`, {
        items: order.items.map((item) => ({ id: item.id, target_domain: (targetDomains[item.id] || '').trim() })),
      });
      await api.patch(`/api/marketing/admin/backlink-orders/${order.id}/`, { status, admin_notes: adminNotes });
    },
    onSuccess: () => { invalidate(); setError(''); onClose(); },
    onError: (err: any) => {
      const data = err.response?.data;
      setError(data?.non_field_errors?.[0] || data?.error || 'Failed to update the order status.');
    },
  });

  const allDomainsFilled = order.items.every((item) => (targetDomains[item.id] || '').trim());
  const busy = saveDetails.isPending || setStatus.isPending;

  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ minWidth: { xs: '92vw', md: 900 }, maxWidth: 1100, maxHeight: '92vh', overflow: 'auto' }}>
        <ModalClose />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography level="h4" sx={{ fontFamily: 'monospace' }}>{order.reference}</Typography>
          <Chip size="sm" variant="soft" color={STATUS_META[order.status]?.color || 'neutral'}>
            {STATUS_META[order.status]?.label || order.status}
          </Chip>
        </Box>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          {order.organization_name} — placed by {order.created_by_email || 'unknown'} on{' '}
          {new Date(order.created_at).toLocaleDateString()} · {money(order.total_amount)} ·{' '}
          Stripe: {order.stripe_payment_intent_id || '-'}
        </Typography>

        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {order.items.map((item, index) => (
            <Card key={item.id} variant="soft" size="sm">
              <Typography level="body-sm" fontWeight="lg">
                {index + 1}. Grade {item.grade.name} ({item.grade.rd_display} RD) — {money(item.price)}
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}>
                {item.url} · Anchor: {item.anchor_text || '-'} · {item.general_niche} / {item.specific_niche}
              </Typography>
              <FormControl size="sm" sx={{ mt: 0.5 }}>
                <FormLabel>Published target domain</FormLabel>
                <Input
                  size="sm"
                  placeholder="https://example.com/article-with-the-backlink"
                  value={targetDomains[item.id] || ''}
                  onChange={(e) =>
                    setTargetDomains((prev) => ({ ...prev, [item.id]: e.target.value }))
                  }
                />
              </FormControl>
            </Card>
          ))}

          <FormControl size="sm">
            <FormLabel>Internal notes (never shown to the customer)</FormLabel>
            <Textarea minRows={2} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
          </FormControl>

          {error && <Alert color="danger" size="sm">{error}</Alert>}

          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button variant="plain" onClick={onClose} disabled={busy}>Close</Button>
            <Button
              variant="outlined"
              color="primary"
              loading={saveDetails.isPending}
              disabled={busy}
              onClick={() => saveDetails.mutate()}
            >
              Save Details
            </Button>
            {order.status === 'complete' ? (
              <Button
                color="warning"
                loading={setStatus.isPending}
                disabled={busy}
                onClick={() => setStatus.mutate('in_process')}
              >
                Reopen (In Process)
              </Button>
            ) : (
              <Button
                color="success"
                loading={setStatus.isPending}
                disabled={busy || !allDomainsFilled}
                onClick={() => setStatus.mutate('complete')}
              >
                Mark Complete
              </Button>
            )}
          </Box>
          {order.status !== 'complete' && !allDomainsFilled && (
            <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'right' }}>
              Fill in every target domain to enable Mark Complete.
            </Typography>
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export const AdminBacklinkOrdersDashboard: React.FC = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [managedOrderId, setManagedOrderId] = useState<number | null>(null);
  const [page,setPage] = useState(1);

  const { data, isLoading, error: loadError, refetch, isFetching } = useQuery({
    queryKey: ['admin-backlink-orders', search, statusFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams({page:String(page),page_size:'25'});
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const qs = params.toString();
      const response = await api.get<any>(`/api/marketing/admin/backlink-orders/${qs ? `?${qs}` : ''}`);
      return response.data;
    },
  });

  const orders: AdminBacklinkOrder[] = data?.results || data || [];
  const totalCount: number = data?.count ?? orders.length;
  const managedOrder = useMemo(
    () => orders.find((o) => o.id === managedOrderId) || null,
    [orders, managedOrderId]
  );

  const columns: ResponsiveColumn<AdminBacklinkOrder>[] = [
    {
      key: 'reference', header: 'Reference', mobileVariant: 'header',
      sortValue: (row) => row.reference,
      render: (o) => (
        <Typography level="title-sm" sx={{ fontFamily: 'monospace' }}>{o.reference}</Typography>
      ),
    },
    {
      key: 'org', header: 'Organization',
      sortValue: (row) => row.organization_name,
      render: (o) => <Typography level="body-sm">{o.organization_name}</Typography>,
    },
    {
      key: 'date', header: 'Placed', width: '12%',
      sortValue: (row) => row.created_at ? new Date(row.created_at) : null,
      render: (o) => (
        <Typography level="body-sm">{new Date(o.created_at).toLocaleDateString()}</Typography>
      ),
    },
    {
      key: 'links', header: 'Links', width: '8%',
      sortValue: (row) => row.items?.length ?? 0,
      render: (o) => (
        <Typography level="body-sm">
          {o.items.filter((i) => i.target_domain).length}/{o.items.length} placed
        </Typography>
      ),
    },
    {
      key: 'total', header: 'Total', width: '10%',
      sortValue: (row) => Number(row.total_amount),
      render: (o) => <Typography level="body-sm">{money(o.total_amount)}</Typography>,
    },
    {
      key: 'status', header: 'Status', width: '12%',
      sortValue: (row) => row.status,
      render: (o) => (
        <Chip size="sm" variant="soft" color={STATUS_META[o.status]?.color || 'neutral'}>
          {STATUS_META[o.status]?.label || o.status}
        </Chip>
      ),
    },
  ];

  return (
    <Box>
      <GradePricingCard />
      <AdminQueryError error={loadError} retry={refetch} message="Could not load backlink orders." />

      <CollapsibleFilters><Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Typography level="title-lg" sx={{ mr: 'auto' }}>
          Backlink Orders {totalCount > 0 && `(${totalCount})`}
        </Typography>
        <Input
          size="sm"
          placeholder="Search reference or organization..."
          value={search}
          onChange={(e) => {setSearch(e.target.value);setPage(1);}}
          sx={{ width: 260 }}
        />
        <Select
          size="sm"
          value={statusFilter}
          onChange={(_, value) => {setStatusFilter((value as string) || '');setPage(1);}}
          sx={{ minWidth: 150 }}
        >
          <Option value="">All statuses</Option>
          <Option value="in_process">In Process</Option>
          <Option value="complete">Complete</Option>
          <Option value="cancelled">Cancelled</Option>
        </Select>
      </Box></CollapsibleFilters>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
          <ResponsiveTable
            rows={orders}
            getRowKey={(o) => o.id}
            columns={columns}
            pagination={{pageSize:25,page,totalCount,onPageChange:next=>{if(!isFetching)setPage(next)},itemNoun:"orders"}}
            rowActions={(o) => (
              <Button size="sm" variant="outlined" color="primary" onClick={() => setManagedOrderId(o.id)}>
                Manage
              </Button>
            )}
            emptyState={
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {loadError ? 'Order list unavailable.' : search || statusFilter ? 'No orders match these filters.' : 'No backlink orders yet.'}
              </Typography>
            }
          />
        </Card>
      )}


      {managedOrder && (
        <OrderFulfillmentModal order={managedOrder} onClose={() => setManagedOrderId(null)} />
      )}
    </Box>
  );
};

export default AdminBacklinkOrdersDashboard;
